import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/browser'
import { kTemplateSelectionApiPath } from '@/constants/constants-api-paths'
import { kErrorCodeAuthError } from '@/constants/constants-error-codes'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { ApiError } from '@/util/errors/api-error'

/**
 * Persist (or replace) a draft's sticky template selection. The backend treats the selection as a
 * singleton per artifact, so this is idempotent.
 */
export async function putTemplateSelection(artifactId: string, templateUserFileId: string, matterId: string): Promise<void> {
  const sentry_transaction_id = nanoid()

  try {
    const token = await getAuth().currentUser?.getIdToken()
    if (!token) throw new Error(kErrorCodeAuthError)

    const apiUrl = `${kPaxtonAppApiRESTBaseUrl()}${kTemplateSelectionApiPath(artifactId)}`
    const response = await fetch(apiUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
        Authorization: `Bearer ${token}`,
        'X-Transaction-Id': sentry_transaction_id,
      },
      body: JSON.stringify({ matter_id: matterId, template_user_file_id: templateUserFileId }),
    })

    if (!response.ok) {
      const data = (await response.json().catch(() => ({}))) as { detail?: unknown }
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentry_transaction_id })
        Sentry.captureException(new Error(`Save template selection failed - status: ${response.status}, detail: ${JSON.stringify(data.detail)}`), {
          extra: { requestUrl: apiUrl, artifactId, matterId, templateUserFileId, data: JSON.stringify(data) },
        })
      })
      throw new ApiError(response.status, `Failed to save template selection: ${response.status}`)
    }
  } catch (error) {
    if (!(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentry_transaction_id })
        Sentry.captureException(error)
      })
    }
    throw error
  }
}
